import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import type { ApropriacaoEntrada, AtualizarImobilizadoDto, CriarImobilizadoDto, ImobilizadoTipo } from '@triade/contracts';
import { useAtualizarImobilizado, useCriarImobilizado, useImobilizado } from '@/api/imobilizados';
import { ApropriacaoPorCentro, type ErroApropriacao } from '@/components/apropriacao';
import { ErroLista } from '@/components/cargos';
import { CodigosExternos } from '@/components/externos/codigos-externos';
import { MensagemCampo } from '@/components/item-cadastro/dados-item';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { ApiError } from '@/lib/api';
import { formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  apropriacaoParaEntrada,
  deImobilizado,
  IMOBILIZADO_VAZIO,
  paraCorpo,
  TIPO_ROTULO,
  validarCorpo,
  type CampoImobilizado,
  type ErroCampoImobilizado,
  type ImobilizadoFormEntrada,
} from './imobilizado-form';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

export interface BuscaImobilizados {
  q?: string;
  itipo?: ImobilizadoTipo;
  isit?: 'INATIVOS' | 'TODOS';
  isemlimite?: 'SIM';
  id?: string;
}

/** Registro de um imobilizado (ou novo). `verCusto` = custo.ver: sem ele nao ha campo de valor nem depreciacao. */
export function ImobilizadoDetalhe({
  id,
  busca,
  podeEditar,
  verCusto,
}: {
  id: string | undefined;
  busca: BuscaImobilizados;
  podeEditar: boolean;
  verCusto: boolean;
}) {
  const navigate = useNavigate();
  const doc = useImobilizado(id);
  const criar = useCriarImobilizado();
  const atualizar = useAtualizarImobilizado();
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [criadoId, setCriadoId] = useState<string | null>(null);
  /** Apropriacao editada na tela (null = nada editado; vale a gravada). `completa` vem do componente (MANUAL fecha 100%). */
  const [apropriacao, setApropriacao] = useState<{ valor: ApropriacaoEntrada | null; completa: boolean } | null>(null);
  const apropriacaoAlterada = apropriacao !== null;
  const [chaveApropriacao, setChaveApropriacao] = useState(0);
  const apropriacaoGravada = useMemo(() => apropriacaoParaEntrada(doc.data?.apropriacao ?? null), [doc.data]);
  const [erroApropriacao, setErroApropriacao] = useState<ErroApropriacao | null>(null);

  const base = useMemo<ImobilizadoFormEntrada>(() => (doc.data ? deImobilizado(doc.data) : IMOBILIZADO_VAZIO), [doc.data]);
  const form = useForm<ImobilizadoFormEntrada>({ defaultValues: base, mode: 'onTouched' });
  const { isDirty, dirtyFields, errors } = form.formState;
  /** Sujo = campos do formulario OU so a apropriacao (que nao passa pelo RHF). */
  const dirty = isDirty || apropriacaoAlterada;
  const tipo = form.watch('tipo');
  const maquina = tipo === 'MAQUINAS_EQUIPAMENTOS';
  const temDataAquisicao = form.watch('dataAquisicao').trim() !== '';

  useEffect(() => {
    if (!form.formState.isDirty) form.reset(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const bloqueio = useBlockerDirty(dirty && criadoId === null);
  useEffect(() => {
    if (criadoId) void navigate({ to: '/cadastros/imobilizados', search: (a) => ({ ...a, id: criadoId }), replace: true });
  }, [criadoId, navigate]);
  const salvando = criar.isPending || atualizar.isPending;
  const alterados = Object.keys(dirtyFields).length + (apropriacaoAlterada ? 1 : 0);
  const lista = { to: '/cadastros/imobilizados' as const, search: { ...(busca.q ? { q: busca.q } : {}), ...(busca.itipo ? { itipo: busca.itipo } : {}), ...(busca.isit ? { isit: busca.isit } : {}), ...(busca.isemlimite ? { isemlimite: busca.isemlimite } : {}) } };

  function apontar(erro: ErroCampoImobilizado) {
    setErroSalvar(erro.mensagem);
    if (erro.campo) form.setError(erro.campo as CampoImobilizado, { message: erro.mensagem });
  }

  async function gravar(v: ImobilizadoFormEntrada) {
    setErroSalvar(null);
    setErroApropriacao(null);
    // MANUAL precisa fechar 100% (e ter linhas completas): editada e incompleta nao vai para a API.
    if (apropriacao && !apropriacao.completa) {
      setErroApropriacao({ code: 'APROPRIACAO_INCOMPLETA', message: 'A apropriação precisa fechar 100% (e ter todas as linhas completas) para salvar.' });
      return;
    }
    const montado = paraCorpo(v, { comCusto: verCusto, apropriacao: apropriacao ? apropriacao.valor : apropriacaoGravada, criando: !id });
    if (!montado.ok) return apontar(montado.erro);
    const validado = validarCorpo(montado.corpo, id ? 'atualizar' : 'criar');
    if (!validado.ok) return apontar(validado.erro);
    try {
      if (id) {
        const gravado = await atualizar.mutateAsync({ id, ...(validado.dados as AtualizarImobilizadoDto) });
        form.reset(deImobilizado(gravado));
      } else {
        const criado = await criar.mutateAsync(validado.dados as CriarImobilizadoDto);
        form.reset(deImobilizado(criado));
        setApropriacao(null);
        setChaveApropriacao((k) => k + 1);
        setSalvoEm(new Date());
        setCriadoId(criado.id);
        return;
      }
      // Gravada: a apropriacao volta a ser a do servidor (rascunho novo, com o que foi gravado).
      setApropriacao(null);
      setChaveApropriacao((k) => k + 1);
      setSalvoEm(new Date());
    } catch (e) {
      setErroSalvar(mensagemApi(e));
      if (e instanceof ApiError) {
        for (const i of e.body?.issues ?? []) {
          const campo = i.path.split('.')[0] as CampoImobilizado;
          if (campo === 'descricao' || campo === 'vidaUtilMeses' || campo === 'valorUnitario') form.setError(campo, { message: i.message });
        }
        const issueApropriacao = e.body?.issues?.find((i) => i.path.startsWith('apropriacao'));
        if (issueApropriacao || e.code?.includes('SOMA') || e.code?.startsWith('APROPRIACAO')) {
          setErroApropriacao({ code: e.code ?? 'APROPRIACAO', message: issueApropriacao?.message ?? e.message });
        }
      }
    }
  }

  if (id && doc.isError) return <section className="rounded-2xl border border-border bg-card p-5"><ErroLista erro={doc.error} onTentar={() => void doc.refetch()} titulo="Não foi possível abrir o imobilizado" /></section>;
  if (id && !doc.data) return <section className="rounded-2xl border border-border bg-card p-5 text-small text-ink-muted" role="status">Carregando imobilizado…</section>;

  const imobilizado = doc.data;
  /** Depreciacao so existe na resposta com custo.ver (a API nao envia a chave sem a permissao). */
  const mostrarDepreciacao = verCusto && imobilizado?.depreciacaoMensal !== undefined;

  return (
    <>
      <section aria-label="Dados do imobilizado" className="rounded-2xl border border-border bg-card p-5">
        <h2 className="text-body font-semibold">{imobilizado ? imobilizado.descricao : 'Novo imobilizado'}</h2>
        {!podeEditar && (
          <p role="status" className="mt-2 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
            <b>Somente leitura.</b> Seu acesso permite consultar os imobilizados, não alterá-los.
          </p>
        )}

        <fieldset disabled={!podeEditar} className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
          <div>
            <label htmlFor="i-tipo" className={ROTULO}>Tipo</label>
            <select id="i-tipo" className={CAMPO} {...form.register('tipo')}>
              {(Object.keys(TIPO_ROTULO) as ImobilizadoTipo[]).map((t) => (
                <option key={t} value={t}>
                  {TIPO_ROTULO[t]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="i-identificador" className={ROTULO}>Identificador</label>
            <input id="i-identificador" maxLength={50} className={CAMPO} {...form.register('identificador')} />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="i-descricao" className={ROTULO}>Descrição</label>
            <input id="i-descricao" maxLength={200} className={CAMPO} aria-invalid={errors.descricao ? true : undefined} aria-describedby={errors.descricao ? 'i-descricao-erro' : undefined} {...form.register('descricao')} />
            <MensagemCampo id="i-descricao-erro" mensagem={errors.descricao?.message} />
          </div>
          {verCusto && (
            <div>
              <label htmlFor="i-valor" className={ROTULO}>Valor unitário (R$)</label>
              <input id="i-valor" inputMode="decimal" className={`${CAMPO} tabular`} aria-invalid={errors.valorUnitario ? true : undefined} aria-describedby={errors.valorUnitario ? 'i-valor-erro' : undefined} {...form.register('valorUnitario')} />
              <MensagemCampo id="i-valor-erro" mensagem={errors.valorUnitario?.message} />
            </div>
          )}
          <div>
            <label htmlFor="i-aquisicao" className={ROTULO}>Data de aquisição</label>
            <input id="i-aquisicao" type="date" className={CAMPO} {...form.register('dataAquisicao')} />
          </div>
          <div>
            <label htmlFor="i-quantidade" className={ROTULO}>Quantidade</label>
            <input id="i-quantidade" inputMode="decimal" className={`${CAMPO} tabular`} {...form.register('quantidade')} />
          </div>
          <div>
            <label htmlFor="i-vida" className={ROTULO}>Vida útil (meses)</label>
            <input id="i-vida" inputMode="numeric" className={`${CAMPO} tabular`} aria-invalid={errors.vidaUtilMeses ? true : undefined} aria-describedby={errors.vidaUtilMeses ? 'i-vida-erro' : undefined} {...form.register('vidaUtilMeses')} />
            <MensagemCampo id="i-vida-erro" mensagem={errors.vidaUtilMeses?.message} />
          </div>
          <div>
            <label htmlFor="i-depreciar-ate" className={ROTULO}>Depreciar até</label>
            <input id="i-depreciar-ate" type="date" className={CAMPO} aria-describedby="i-depreciar-ate-dica" {...form.register('depreciarAte')} />
            <p id="i-depreciar-ate-dica" className="mt-1 text-caption text-ink-muted">Opcional. Manda sobre a data de aquisição e a vida útil: deprecia até o mês dessa data.</p>
          </div>
          <div>
            <label htmlFor="i-idade" className={ROTULO}>Idade (meses)</label>
            {temDataAquisicao ? (
              <>
                <input id="i-idade" readOnly inputMode="numeric" className={`${CAMPO} tabular bg-line-soft`} value={imobilizado ? String(imobilizado.idadeMeses) : ''} placeholder="Após salvar" aria-describedby="i-idade-dica" />
                <p id="i-idade-dica" className="mt-1 text-caption text-ink-muted">Calculada pela data de aquisição</p>
              </>
            ) : (
              <>
                <input id="i-idade" inputMode="numeric" className={`${CAMPO} tabular`} aria-describedby="i-idade-dica" {...form.register('idadeMeses')} />
                <p id="i-idade-dica" className="mt-1 text-caption text-ink-muted">Opcional. Informar 0 conta como idade informada.</p>
              </>
            )}
          </div>
          <label htmlFor="i-depreciavel" className="flex cursor-pointer items-center gap-2 self-end">
            <input id="i-depreciavel" type="checkbox" className="size-[18px] accent-primary" {...form.register('depreciavel')} />
            Depreciável
          </label>
          <div>
            <label htmlFor="i-marca" className={ROTULO}>Marca</label>
            <input id="i-marca" maxLength={100} className={CAMPO} {...form.register('marca')} />
          </div>
          <div>
            <label htmlFor="i-modelo" className={ROTULO}>Modelo</label>
            <input id="i-modelo" maxLength={100} className={CAMPO} {...form.register('modelo')} />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="i-observacao" className={ROTULO}>Observação</label>
            <textarea id="i-observacao" rows={2} maxLength={500} className="w-full rounded-[9px] border border-input bg-card px-2.5 py-2 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70" {...form.register('observacao')} />
          </div>
          {maquina && (
            <>
              <div>
                <label htmlFor="i-consumo" className={ROTULO}>Consumo (W/h)</label>
                <input id="i-consumo" inputMode="decimal" className={`${CAMPO} tabular`} aria-invalid={errors.consumoWh ? true : undefined} {...form.register('consumoWh')} />
                <MensagemCampo id="i-consumo-erro" mensagem={errors.consumoWh?.message} />
              </div>
              <div>
                <label htmlFor="i-uso" className={ROTULO}>Uso (h/dia)</label>
                <input id="i-uso" inputMode="decimal" className={`${CAMPO} tabular`} aria-invalid={errors.usoHorasDia ? true : undefined} {...form.register('usoHorasDia')} />
                <MensagemCampo id="i-uso-erro" mensagem={errors.usoHorasDia?.message} />
              </div>
            </>
          )}
          <label htmlFor="i-ativo" className="flex cursor-pointer items-center gap-2 self-end">
            <input id="i-ativo" type="checkbox" disabled={!id} className="size-[18px] accent-primary" {...form.register('ativo')} />
            Ativo
            {!id && <span className="text-small text-ink-muted">Todo imobilizado novo entra ativo.</span>}
          </label>
        </fieldset>

        {imobilizado?.semLimiteDepreciacao && (
          <p role="status" data-testid="aviso-sem-limite" className="mt-4 rounded-lg border border-warning-dot bg-warning-bg px-3 py-2 text-caption text-warning-ink">
            Sem limite de depreciação: informe a data de aquisição ou a data limite.
          </p>
        )}
        {imobilizado && !imobilizado.depreciavel && imobilizado.observacao && (
          <p data-testid="aviso-nao-depreciavel" className="mt-4 rounded-lg border border-line-soft bg-line-soft px-3 py-2 text-small text-ink-body">
            <b>Não depreciável:</b> {imobilizado.observacao}
            {verCusto && imobilizado.depreciacaoSeDepreciavel !== undefined ? ` Se for depreciável entraria ${formatarMoeda(imobilizado.depreciacaoSeDepreciavel)} por mês.` : null}
          </p>
        )}
        {imobilizado?.importadoXarp && imobilizado.depreciavel && !imobilizado.dataAquisicao && !imobilizado.depreciarAte && (
          <p role="status" data-testid="aviso-idade-estatica" className="mt-4 rounded-lg border border-warning-dot bg-warning-bg px-3 py-2 text-caption text-warning-ink">
            A idade veio do XARP antigo, onde ela não avançava com o tempo. Informe a data de aquisição para a idade passar a contar sozinha.
          </p>
        )}

        {mostrarDepreciacao && imobilizado && (
          <dl className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 rounded-xl border border-border bg-line-soft px-4 py-3 text-small">
            <div>
              <dt className="text-caption font-medium text-ink-secondary">Depreciação mensal</dt>
              <dd className="tabular mt-1" data-testid="depreciacao-mensal">{formatarMoeda(imobilizado.depreciacaoMensal)}</dd>
            </div>
            <div>
              <dt className="text-caption font-medium text-ink-secondary">Depreciação total</dt>
              <dd className="tabular mt-1" data-testid="depreciacao-total">{formatarMoeda(imobilizado.depreciacaoTotal)}</dd>
            </div>
          </dl>
        )}

        <div className="mt-5">
          <h4 className="mb-2 text-small font-semibold">Apropriação por centro de custo</h4>
          <div className={cn('mt-3', !podeEditar && 'opacity-80')}>
            <ApropriacaoPorCentro
              key={`${id ?? 'novo'}-${chaveApropriacao}-${doc.data?.updatedAt ?? ''}`}
              value={apropriacaoGravada}
              onChange={(valor, situacao) => setApropriacao({ valor, completa: situacao?.completa ?? valor !== null })}
              resolvida={imobilizado?.apropriacao ?? null}
              referTo="IMOBILIZADO"
              disabled={!podeEditar || salvando}
              erro={erroApropriacao}
            />
          </div>
        </div>

        {imobilizado && (
          <div className="mt-5 border-t border-border pt-4">
            <CodigosExternos entidade="imobilizados" id={imobilizado.id} editavel={podeEditar} />
          </div>
        )}

        <p className="mt-4 text-caption text-ink-muted">Valor e depreciação são calculados pela API. Consumo e uso valem só para máquinas e equipamentos.</p>
      </section>

      {podeEditar && (
        <RecordToolbar
          dirty={dirty}
          saving={salvando}
          savedAt={salvoEm}
          erro={erroSalvar}
          novo={!id}
          canPrev={false}
          canNext={false}
          alteradosCount={dirty ? alterados : undefined}
          onList={() => void navigate({ ...lista, replace: true })}
          onSave={() => void form.handleSubmit(gravar, () => setErroSalvar('Corrija os campos destacados'))()}
          onNew={() => void navigate({ to: '/cadastros/imobilizados', search: (a) => ({ ...a, id: 'novo' }) })}
          onUndo={() => {
            form.reset(base);
            setErroSalvar(null);
            setErroApropriacao(null);
            setApropriacao(null);
            setChaveApropriacao((k) => k + 1);
          }}
        />
      )}
      <SaidaComAlteracoesDialog {...bloqueio} />
    </>
  );
}
